import React from 'react';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

import {ClassworkRow} from 'client/TeacherDashboard/components/ClassworkRow';
import {formatDateRange, formatDateTime, getDueCopy} from 'client/TeacherDashboard/rowDateCopy';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import type {SkillPathRowData} from './types';

const getSubtitleSections = (row: SkillPathRowData, showExactDueDate: boolean): string[] => {
  if (row.status === 'scheduled') {
    return [
      row.minStartDate
        ? `Starts ${formatDateRange(row.minStartDate, row.maxStartDate, formatDateTime)}`
        : 'Scheduled'
    ];
  }
  const dueCopy = getDueCopy(row, showExactDueDate);
  if (row.status === 'closed') {
    return dueCopy ? ['Closed', dueCopy] : ['Closed'];
  }
  return [dueCopy || 'Open'];
};

const STATUS_DISPLAY: Record<
  SkillPathRowData['status'],
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'}
};

interface Props {
  row: SkillPathRowData;
  /** Clicking the row opens the skill modal in view mode. */
  onOpen: (row: SkillPathRowData) => void;
  /** The edit icon opens the same modal in edit mode. */
  onEdit: (row: SkillPathRowData) => void;
  /** The report icon opens the teacher report drilled into this skill. */
  onReport: (row: SkillPathRowData) => void;
}

const SkillPathRowBase = ({row, onOpen, onEdit, onReport}: Props) => {
  const {
    color: statusColor,
    icon: statusIcon,
    iconStyle: statusIconStyle
  } = STATUS_DISPLAY[row.status];

  return (
    <ClassworkRow
      actions={
        <>
          <IconButton
            icon='chart-line'
            iconStyle='regular'
            label='View Report'
            tooltip='View Report'
            size='s'
            onClick={() => onReport(row)}
          />
          <IconButton
            icon='pencil'
            iconStyle='regular'
            label='Edit Skill Path'
            tooltip='Edit Skill Path'
            size='s'
            onClick={() => onEdit(row)}
          />
        </>
      }
      icon='diagram-project'
      iconStyle='regular'
      renderSubtitle={(isHovered) => (
        <Text as='div' className='truncate' color={statusColor} size='s'>
          {statusIcon ? (
            <Icon
              className='mr-x0-half text-xs!'
              color={statusColor}
              icon={statusIcon}
              iconStyle={statusIconStyle}
            />
          ) : null}
          {getSubtitleSections(row, isHovered).map((part, index) => (
            <React.Fragment key={part}>
              {index > 0 && <span className='mx-x1'>•</span>}
              {part}
            </React.Fragment>
          ))}
        </Text>
      )}
      data-testid='skill-path-row'
      title={
        <Text
          as={MarkdownRendererV2}
          className='content-primary truncate'
          size='m'
          text={row.skillName}
        />
      }
      onOpen={() => onOpen(row)}
    />
  );
};

export const SkillPathRow = React.memo(SkillPathRowBase);
